import {
  EstadoFactura,
  InvoiceAvisoEstado,
  InvoiceCambioEstado,
  InvoiceSummary,
} from '../models/invoice.model';

/**
 * Cartel que corresponde a un cambio de estado de comprobante.
 * `PENDING` (reintento del Gateway) no genera cartel para no alarmar.
 */
export function avisoPorCambioEstado(cambio: InvoiceCambioEstado): InvoiceAvisoEstado | null {
  const receptor = cambio.denominacion_receptor || 'el receptor';
  const tipo = cambio.tipo_comprobante;

  switch (cambio.estado) {
    case 'APPROVED': {
      const numero = cambio.comprobante_numero
        ? ` N° ${String(cambio.comprobante_numero).padStart(8, '0')}`
        : '';
      return {
        tipo: 'success',
        texto: `✅ ${tipo}${numero} aprobada por AFIP para ${receptor}. CAE: ${cambio.cae}`,
        duracionMs: 8000,
      };
    }
    case 'REJECTED': {
      let motivo = 'AFIP rechazó el comprobante sin especificar el motivo.';
      const obs = cambio.observaciones;
      if (obs) {
        const mensajes = obs['mensajes'];
        if (Array.isArray(mensajes) && mensajes.length > 0) {
          motivo = (mensajes as string[]).join(' | ');
        } else if (typeof obs['gateway_error'] === 'string') {
          motivo = obs['gateway_error'];
        }
      }
      return { tipo: 'error', texto: `❌ ${tipo} para ${receptor} rechazada por AFIP: ${motivo}`, duracionMs: 10000 };
    }
    case 'FATAL_ERROR':
      return {
        tipo: 'error',
        texto: `⛔ ${tipo} para ${receptor}: se agotaron los reintentos automáticos. Requiere intervención manual.`,
        duracionMs: 12000,
      };
    default:
      return null;
  }
}

/**
 * Una sucursal solo debe ver los carteles de lo suyo. Sin sucursal de trabajo (modo global)
 * o con un comprobante sin sucursal, se muestra siempre.
 */
export function avisoAplicaASucursal(branchIdComprobante: string | null, sucursalDeTrabajo: string | null): boolean {
  return !sucursalDeTrabajo || !branchIdComprobante || branchIdComprobante === sucursalDeTrabajo;
}

/** Reemplaza en la página actual la fila con el mismo `id` por la versión actualizada. */
export function reemplazarFila(filas: InvoiceSummary[], actualizada: InvoiceSummary): InvoiceSummary[] {
  return filas.map(fila => (fila.id === actualizada.id ? actualizada : fila));
}

/** Indica si una fila sigue cumpliendo el filtro de estado del listado ('' = sin filtro). */
export function cumpleFiltroEstado(estado: EstadoFactura, filtro: EstadoFactura | ''): boolean {
  return !filtro || estado === filtro;
}

/** Ids de las filas todavía pendientes (a las que se les hace seguimiento de respaldo). */
export function idsPendientes(filas: InvoiceSummary[]): string[] {
  return filas.filter(fila => fila.estado === 'PENDING').map(fila => fila.id);
}

/**
 * Una Nota de Crédito que cambia de estado también modifica OTRA fila (la factura que anula,
 * con su marca `ya_tiene_nc`), así que en ese caso no alcanza con refrescar solo la propia.
 */
export function afectaOtrasFilas(cambio: Pick<InvoiceCambioEstado, 'tipo_comprobante'>): boolean {
  return cambio.tipo_comprobante.toLowerCase().includes('nota de cr');
}
